import SmallEditDialog from "@/components/common/SmallEditDialog";
import EditInjectionDetailsOrthopaedicArea from "@/features/injection/components/EditInjectionDetailsOrthopaedicArea";
import EditInjectionDetailsSide from "@/features/injection/components/EditInjectionDetailsSide";
import SharedAttributeDatePicker from "@/features/shared_attributes/components/SharedAttributeDatePicker";
import SharedAttributeHospitalNumberForm from "@/features/shared_attributes/components/SharedAttributeHospitalNumberForm";
import type { StatefulDiaryT } from "@/types/database/diaries";
import { AlertCircle } from "lucide-react";
import { useMissingDetails } from "../hooks/useMissingDetails";

interface DiaryCardProps {
	diary: StatefulDiaryT;
}

const MissingDiaryDetails: React.FC<DiaryCardProps> = ({ diary }) => {
	const { missingDetails, hasMissingDetails } = useMissingDetails(diary);

	if (!hasMissingDetails) {
		return null;
	}
	const hasMissingDate = missingDetails?.trackers?.missingDate?.length > 0;
	const hasMissingHospitalNumber =
		missingDetails?.trackers?.missingHospitalNumber?.length > 0;
	const hasMissingSide = missingDetails?.injections?.missingSide?.length > 0;
	const hasMissingOrthopaedicArea =
		missingDetails?.injections?.missingOrthopaedicArea?.length > 0;

	// Get the first injection detail that needs updating
	const injectionDetail =
		diary.events_view?.[0]?.injections_view?.[0]?.injection_details?.[0];

	return (
		<div className="flex items-start gap-2 bg-destructive/10 p-3 rounded-md">
			<AlertCircle size={20} className="mt-1 text-destructive shrink-0" />
			<div className="space-y-2 text-sm">
				<p className="font-medium text-destructive">Missing Information</p>
				<ul className="space-y-1.5">
					{hasMissingDate && (
						<li>
							<SmallEditDialog label="Injection Date">
								<SharedAttributeDatePicker
									sharedAttributes={diary.shared_attributes}
									dateType="reference_date"
								/>
							</SmallEditDialog>
						</li>
					)}
					{hasMissingHospitalNumber && (
						<li>
							<SmallEditDialog label="Hospital Number">
								<SharedAttributeHospitalNumberForm
									sharedAttributes={diary.shared_attributes}
									initialHospitalNumber={
										diary.shared_attributes?.hospital_number
									}
								/>
							</SmallEditDialog>
						</li>
					)}
					{hasMissingSide && injectionDetail?.injection_detail_id && (
						<li>
							<SmallEditDialog label="Side of Procedure">
								<EditInjectionDetailsSide
									diaryId={diary.diary_id}
									injectionDetailId={injectionDetail.injection_detail_id}
									side={injectionDetail.side ?? "N/A"}
								/>
							</SmallEditDialog>
						</li>
					)}
					{hasMissingOrthopaedicArea &&
						injectionDetail?.injection_detail_id && (
							<li>
								<SmallEditDialog label="Anatomic Area">
									<EditInjectionDetailsOrthopaedicArea
										diaryId={diary.diary_id}
										injectionDetailId={injectionDetail.injection_detail_id}
										orthopaedicArea={injectionDetail.orthopaedic_area ?? ""}
									/>
								</SmallEditDialog>
							</li>
						)}
				</ul>
			</div>
		</div>
	);
};

export default MissingDiaryDetails;
